import type * as THREE from 'three';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';

// three's UnrealBloomPass blurs each mip with a Gaussian whose sigma IS its
// kernel radius, cut off at that radius: the last tap still weighs e^-0.5 of
// the centre. Any compact source far over the bloom threshold (a warhead, a
// reactor breach, a Lance strike) comes out of that kernel as a halo with
// straight edges, a box, at mip 2 to 4 (about 130 and 290 px wide at 720p).
// This rewrites each blur to a real Gaussian of the same second moment (a
// 1-sigma-truncated Gaussian has a standard deviation of ~0.54 sigma), carried
// out to ~2.6 sigma where the tail is 3% of the centre, so the bloom keeps its
// size and loses the box. The blur divides by its own weight sum, so energy is
// unchanged. Cost: ~1.4x the taps on the low-resolution mips.

const SPREAD = 0.55;
const REACH = 1.4;

function roundKernel(m: THREE.ShaderMaterial): void {
  const r0 = Number(m.defines?.['KERNEL_RADIUS']);
  if (!(r0 > 0) || m.userData['roundKernel'] === true) return;
  const sigma = SPREAD * r0;
  const taps = Math.ceil(REACH * r0) + 1;
  const coefficients: number[] = [];
  for (let i = 0; i < taps; i++) coefficients.push(Math.exp((-0.5 * i * i) / (sigma * sigma)));
  m.defines['KERNEL_RADIUS'] = taps;
  const u = m.uniforms['gaussianCoefficients'];
  if (u) u.value = coefficients;
  m.userData['roundKernel'] = true;
  m.needsUpdate = true;
}

/** Give a constructed bloom pass round (untruncated Gaussian) blur kernels. */
export function roundBloomKernel(pass: UnrealBloomPass): void {
  for (const m of pass.separableBlurMaterials) roundKernel(m);
}

/**
 * Run `make` with every UnrealBloomPass it constructs keeping three's stock,
 * box-drawing kernels even through `roundBloomKernel`: the capture's
 * `--inject box-bloom` control, which the square bar must fail.
 */
export function withBoxBloomKernels<T>(make: () => T): T {
  const proto = UnrealBloomPass.prototype as unknown as { getSeperableBlurMaterial(r: number): THREE.ShaderMaterial };
  const original = proto.getSeperableBlurMaterial;
  proto.getSeperableBlurMaterial = function (this: UnrealBloomPass, r: number): THREE.ShaderMaterial {
    const m = original.call(this, r);
    m.userData['roundKernel'] = true;
    return m;
  };
  try {
    return make();
  } finally {
    proto.getSeperableBlurMaterial = original;
  }
}
